import { ImagePaths } from '../../lib/imageUrls.js';

export const aiQuickStartHr = {

    id: 59,
    slug: 'ai-quick-start-hr',
    ogImage: '/og/courses/ai-quick-start-hr.jpg',
    courseCode: 'AI-HR-QS-001',
    title: 'AI Quick Start for HR Teams',
    subtitle: 'One evening of practical AI for the documents HR writes every week',
    valueProposition: 'Use everyday AI tools to draft job ads, position descriptions, interview guides, and policy updates faster, while knowing exactly which employee information must never enter a prompt.',
    duration: '4 hours',
    students: 'New',
    level: 'Foundational',
    image: ImagePaths.COURSE_2,
    shortDescription: 'AI-HR-QS-001 — Tool-agnostic AI for HR covering job ads, position descriptions, interview guides, and prompt red-lines for Australian HR practitioners.',
    price: {
      aud: 395,
      currency: 'AUD'
    },
    stripeConfig: {
    priceId: 'price_1U3WMsKu1nyc322UWBjho07j',
    productId: 'prod_V3deMXh1iRWoLZ'
  },

    // Drives the cohort registration panel on the course page. No `cohort`
    // is set until the next run is dated, so the form and the confirmation
    // email say "the next cohort".
    // Presence of this field is what renders the form — it is deliberately
    // separate from `promo` so retiring the discount does not silently
    // remove the waitlist as well.
    waitlist: {
      label: 'Next cohort'
    },

    snapshot: {
      whoItsFor: [
        'HR coordinators and administrators writing job ads, letters, and policy updates',
        'HR business partners and generalists in small and mid-sized organisations',
        'Recruiters and talent acquisition officers drafting ads and interview guides',
        'People and culture managers in organisations with no dedicated AI training budget',
        'Sole-charge HR practitioners who are the entire HR function'
      ],
      accreditation: 'Grounded in the Privacy Act 1988 and the Australian Privacy Principles, the Fair Work Act 2009, and Australian anti-discrimination legislation including the Sex Discrimination Act 1984 and Racial Discrimination Act 1975.'
    },

    logistics: {
      classSize: 'Maximum 20 participants so everyone gets feedback on their own drafts',
      requirements: [
        'Computer with a working microphone and internet access (minimum 10 Mbps)',
        'Access to at least one general AI assistant such as ChatGPT, Copilot, Claude, or Gemini',
        'Two or three real HR documents you would like to improve, with names and identifiers removed',
        'Working knowledge of your own HR processes such as recruitment, onboarding, or policy',
        'No technical, Copilot, or prior AI experience required'
      ]
    },

    overview: 'HR teams write more than almost anyone else in an organisation: job ads, position descriptions, interview guides, reference check questions, offer and rejection letters, policy updates, toolbox talks, and endless plain-English summaries of things nobody wants to read. This four-hour evening course shows you how to get real drafting help from ordinary AI tools on exactly those documents, using whichever assistant your organisation has approved. It is deliberately tool-agnostic, so nothing here depends on a Microsoft 365 Copilot licence or a particular HR information system. Every exercise uses realistic Australian HR material, and every draft you produce is one you could take back to work the next day. The final module is the one participants tell us matters most: a clear set of red-lines covering what must never go into a prompt. Employee records, performance and disciplinary notes, salary, health information, protected attributes, and complaint details are handled under the Privacy Act 1988 and the Australian Privacy Principles, and we cover practical sanitisation patterns so you can still get help with sensitive work without exposing anyone. The price is 395 AUD including GST, delivered in a single Tuesday evening session.',

    outcomes: [
      'Draft a compliant, inclusive job advertisement from a rough brief and refine it for tone and reach',
      'Build a position description from scratch or modernise an outdated one against a real role',
      'Generate structured, behaviourally anchored interview guides and reference check questions',
      'Turn dense policy or legislative text into plain-English summaries staff will actually read',
      'Draft sensitive correspondence such as offer, rejection, and change-of-conditions letters with an appropriate tone',
      'Identify the employee information that must never enter a prompt under the Privacy Act 1988',
      'Sanitise a real HR task so you keep the AI assistance and remove the personal information',
      'Review AI-generated HR content for discriminatory language, invented facts, and unsafe advice'
    ],

    audience: [
      'HR coordinators, officers, and administrators',
      'HR business partners and HR generalists',
      'Recruiters, talent acquisition officers, and hiring managers',
      'People and culture managers in small and mid-sized organisations',
      'Office managers who carry the HR function alongside other duties',
      'Payroll and workplace relations officers who draft employee correspondence'
    ],

    prerequisites: [
      'Familiarity with at least one HR process such as recruitment, onboarding, performance, or policy',
      'Comfortable using email, a word processor, and a web browser',
      'Access to a general AI assistant, or willingness to create a free account before the session',
      'No technical, programming, or Microsoft Copilot experience required',
      'Prompt Engineering Basics is helpful but not required'
    ],

    modules: [
      {
        title: 'AI That Actually Helps in HR',
        duration: '1 hour',
        topics: [
          'Where AI genuinely saves HR time, and the three places it reliably wastes it',
          'A five-part prompt structure applied to a real HR drafting task, start to finish',
          'Working tool-agnostically: getting the same result from ChatGPT, Copilot, Claude, or Gemini',
          'Building a reusable HR prompt library instead of retyping from scratch each time',
          'Setting up a sanitised working file so nothing identifying leaves your desk'
        ]
      },
      {
        title: 'Recruitment Documents: Ads, Position Descriptions, and Interview Guides',
        duration: '1 hour',
        topics: [
          'Drafting a job advertisement from a rough brief, then tuning tone, length, and candidate appeal',
          'Building or modernising a position description with genuine inherent requirements',
          'Generating structured, behaviourally anchored interview questions with scoring guidance',
          'Drafting reference check questions and candidate communication templates',
          'Reviewing every draft for gendered, ageist, or otherwise discriminatory language under Australian anti-discrimination law'
        ]
      },
      {
        title: 'Policy, Correspondence, and Plain-English Summarising',
        duration: '1 hour',
        topics: [
          'Turning a policy, award clause, or legislative change into a summary staff will read',
          'Drafting toolbox talks, intranet notices, and manager briefing notes from a single source document',
          'Writing offer, rejection, probation, and change-of-conditions letters with an appropriate tone',
          'Summarising long consultation feedback or survey comments into themes without quoting individuals',
          'Fact-checking discipline: why AI invents award clauses and entitlements, and how to catch it every time'
        ]
      },
      {
        title: 'Never Put This In a Prompt: HR Red-Lines and Privacy',
        duration: '1 hour',
        topics: [
          'The red-line list: employee names and identifiers, salary, performance and disciplinary notes, health and medical information, protected attributes, complaint and investigation detail, and bank details',
          'Employee records and the Australian Privacy Principles under the Privacy Act 1988, in plain language',
          'What happens to text you paste into a consumer AI tool, and why free accounts are the highest-risk option',
          'Sanitisation patterns: anonymising, generalising, and splitting a task so the sensitive part never leaves your system',
          'When to stop and escalate: investigations, terminations, medical matters, and anything heading towards the Fair Work Commission'
        ]
      }
    ],

    format: {
      type: 'Live online evening session, Tuesday 6-10pm',
      includes: [
        '4 hours of live, instructor-led training in one evening',
        'Hands-on drafting exercises using realistic Australian HR documents',
        'An HR prompt library covering ads, position descriptions, interview guides, and letters',
        'A one-page HR red-lines card covering what must never enter a prompt',
        'Sanitisation patterns for working safely with sensitive HR tasks',
        'Certificate of completion suitable for continuing professional development records',
        'Live Q&A throughout, with a dedicated segment on your own documents'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Live online evening cohorts run on scheduled dates. Register your interest and we will confirm the next dates.'
    },

    // Real, dated sessions. The Enrol button is gated on there being a
    // future entry here — without one the page offers the waitlist instead,
    // because taking $395 for an unidentified session is not a supply
    // commitment under Australian Consumer Law. (#341)
    // Cohort opens the week of Monday 21 September 2026. NSW daylight saving
    // begins Sunday 4 October 2026 — see prompt-engineering-basics.js for why
    // the October run carries +11:00 rather than +10:00.
    sessions: [
      { start: '2026-09-22T18:00:00+10:00', end: '2026-09-22T22:00:00+10:00', capacity: 20 },
      { start: '2026-09-29T18:00:00+10:00', end: '2026-09-29T22:00:00+10:00', capacity: 20 },
      { start: '2026-10-06T18:00:00+11:00', end: '2026-10-06T22:00:00+11:00', capacity: 20 }
    ],

    pathways: [],

    skillsTaught: [
      'AI-assisted HR document drafting',
      'Inclusive job ads and position descriptions',
      'Structured interview guide design',
      'Plain-English policy summarising',
      'Employee privacy red-lines for AI prompts'
    ],

    hasCapstone: false,

    nextCourses: [
      'ai-for-hr-m365'
    ],

    faqs: [
      {
        question: 'How is this different from AI for HR in Microsoft 365?',
        answer: 'AI for HR in Microsoft 365 is a 12-hour intermediate program for HR leaders governing an enterprise Copilot and Viva rollout, covering sensitivity labels, Copilot Studio agents, HRIS integration, and obligations such as the EU AI Act. This four-hour course is the opposite end: tool-agnostic, hands-on drafting for any HR practitioner, with no licence requirement and no governance project attached. Many people do this one first and the Microsoft 365 course later.'
      },
      {
        question: 'Do I need a Microsoft 365 Copilot licence?',
        answer: 'No. Nothing in this course depends on Copilot, Viva, or any paid tool. We teach the principles and demonstrate on the widely available assistants, so you can apply everything with whatever your organisation has approved, including free tiers. If you do have Copilot, everything still applies.'
      },
      {
        question: 'What exactly counts as employee information I should not paste in?',
        answer: 'Names and employee numbers, salary and remuneration detail, performance and disciplinary notes, health and medical information, protected attributes such as age, race, disability, or pregnancy, complaint and investigation content, and bank or superannuation details. The red-lines module works through each of these with real examples and gives you a one-page card to keep. We also cover how to sanitise a task so you can still get help.'
      },
      {
        question: 'Will AI write our policies for us?',
        answer: 'No, and the course is direct about this. AI is genuinely good at restructuring, summarising, and drafting plain-English versions of policy you already have. It is unreliable at stating Australian award entitlements, Fair Work obligations, and legislative requirements, and it will invent clause numbers with total confidence. Every policy output needs a human with the actual instrument in front of them.'
      },
      {
        question: 'Is AI-assisted recruitment allowed in Australia?',
        answer: 'Using AI to draft an advertisement, a position description, or an interview guide is ordinary drafting assistance and is uncontroversial. Using AI to screen, rank, or select candidates is a different activity with discrimination and transparency risk, and it is out of scope here. We flag the boundary clearly so you know which side of it you are on.'
      },
      {
        question: 'Do we need to tell employees we are using AI?',
        answer: 'For drafting assistance where no employee information leaves your systems, generally no. Where AI touches employee data, monitoring, or decisions affecting employment, transparency and consultation obligations can apply, including consultation clauses in modern awards and enterprise agreements. The course flags where those questions start so you can raise them internally before they become a problem.'
      },
      {
        question: 'Can we enrol our whole HR team?',
        answer: 'Yes, and it works well because the team leaves with a shared prompt library and a shared understanding of the red-lines. For five or more participants from the same organisation, contact us for a private quote. Private cohorts using your own document types in the exercises are also arranged on request.'
      },
      {
        question: 'Is the price inclusive of GST?',
        answer: 'Yes. The price is 395 AUD including GST per person, which is the full amount payable. A tax invoice is issued automatically on enrolment.'
      }
    ]
};
